import { Directive, inject } from '@angular/core';
import { formatDate } from '@angular/common';
import { Competition, MediaData, StreamPlayItem, Team } from '../types/stream-play.type';
import { StreamPlayLiveService } from '../live-service/stream-play-live.service';
import { BaseComponentDirective } from '@streamamg/amg-common';
import { Observable, ReplaySubject } from 'rxjs';

export interface CustomMediaData extends MediaData {
  isEntryCurrentlyLive?: boolean;
}

@Directive()
export class StreamPlayDirective extends BaseComponentDirective {
  public isMediaLive: boolean;
  public mediaDataEntryIsLive$ = new Observable<CustomMediaData>();
  public mediaDataEntryIsLiveSubject = new ReplaySubject<CustomMediaData>;

  public streamPlayItem: StreamPlayItem;
  public itemMediaData: { video: CustomMediaData[]; audio: CustomMediaData[]; } | undefined;

  public streamPlayLiveService = inject(StreamPlayLiveService);

  constructor() {
    super();

    this.mediaDataEntryIsLive$ = this.mediaDataEntryIsLiveSubject.asObservable();

    const base: any = {
      id: null,
      name: '',
    };

    const date = '1970-01-01T00:00:00.000Z';

    this.streamPlayItem = ({
      homeTeam: base as Team,
      awayTeam: base as Team,
      competition: base as Competition,
      startDate: date,
      endDate: date,
    } as any);

  }

  private static generateMediaObject(mediaData: MediaData): CustomMediaData {
    return {
      ...mediaData,
      isEntryCurrentlyLive: false,
    };
  }

  public init(): void {

    this.itemMediaData = this.flattenMediaData(this.streamPlayItem.mediaData);
  }

  public setItemData(fixtureData: StreamPlayItem) {
    this.streamPlayItem = fixtureData;
  }

  public isThumbnailAvailable(): boolean {
    return <boolean><unknown>this.streamPlayItem.thumbnail && this.streamPlayItem.thumbnail.length > 0;
  }

  public getMediaData(): { video: CustomMediaData[]; audio: CustomMediaData[] } | undefined {
    return this.itemMediaData;
  }

  public getFormattedDate(dateFormat: string, locale?: string): string {
    return formatDate(this.streamPlayItem.startDate, dateFormat, locale || 'en');
  }

  public getEntryId(mediaType: 'video' | 'audio'): string | null {
    return this.itemMediaData && this.itemMediaData[mediaType] && this.itemMediaData[mediaType][0]
      ? this.itemMediaData[mediaType][0].entryId
      : null;
  }

  protected triggerFixtureIsLive(mediaType?: 'video' | 'audio'): void {

    mediaType = mediaType || 'video';
    if (this.itemMediaData && this.itemMediaData[mediaType]?.length) {
      this.itemMediaData[mediaType].forEach((entry, index) => {

        if (this.itemMediaData && mediaType) {
          this.itemMediaData[mediaType][index] = entry;

          this.streamPlayLiveService.entryLiveApiCall(this.streamPlayItem, entry);

          const itemKey = `${this.streamPlayItem.id}-${entry.entryId}-${entry.mediaType}`;
          this.streamPlayLiveService.getLiveEntry(itemKey).subscribe((isLive) => {

            this.itemMediaData[mediaType][index].isEntryCurrentlyLive = isLive;
            this.isMediaLive = !!this.itemMediaData[mediaType].find((item) => item.isEntryCurrentlyLive);

            this.mediaDataEntryIsLiveSubject.next(this.itemMediaData[mediaType][index]);
            this.cdr.markForCheck();
          });

        }
      });
    } else {
      this.isMediaLive = false;
      this.mediaDataEntryIsLiveSubject.next({isEntryCurrentlyLive: false});
    }
  }

  protected forceCheckFixtureIsLive(mediaType?: 'video' | 'audio'): void {
    mediaType = mediaType || 'video';
    if (this.itemMediaData && this.itemMediaData[mediaType]?.length) {
      this.itemMediaData[mediaType].forEach((entry, index) => {

        if (this.itemMediaData && mediaType) {
          this.itemMediaData[mediaType][index] = entry;

          this.streamPlayLiveService.entryLiveApiCall(this.streamPlayItem, entry, true, true);
        }
      });
    }
  }

  private flattenMediaData(
    mediaData: MediaData[],
  ): { video: CustomMediaData[]; audio: CustomMediaData[] } {
    const flatMediaData: { video: CustomMediaData[]; audio: CustomMediaData[] } = {
      video: [],
      audio: [],
    };

    if (mediaData) {
      mediaData.forEach((value) => {
        // @ts-ignore
        flatMediaData[value.mediaType].push(StreamPlayDirective.generateMediaObject(value));
      });
    }
    return flatMediaData;
  }
}
